import Button from 'components/ui/Button/Button';
import FloatingLabelInput from 'components/ui/Inputs/FloatingLabelInput';
import { useCardContext } from 'context/hooks/useCardContext';
import { usePageContext } from 'context/hooks/usePageContext';
import { useTransactionContext } from 'context/hooks/useTransactionContext';
import { PAGE_SUB } from 'context/pageContext';
import { useFormik } from 'formik';
import React from 'react';
import { CardData, PaymentHandlerFactory } from 'services/paymentHandlers';
import { getErrorMessage } from 'services/paymentHandlers/apiError';
import { PAYMENT_PROVIDER } from 'types/enums';
import { usePayableAmount } from 'context/hooks/usePayableAmount';
import { checkResponse, getResponseMessage, isErrorBody } from 'utils';
import { validatePin } from 'utils/validation';

import { ReactComponent as Spinner } from '../../../assets/icons/spinner.svg';
import Icon from 'components/ui/Icon/Icon';

const PinAuth = ({
  setUrl,
}: {
  setUrl: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const {
    ceviantTransactionId,
    merchantCollectionPublicKey,
    customerEmail,
    provider,
    customerPhone,
    publicKey,
    changeTransactionDetails,
  } = useTransactionContext();
  const { changeSub, changePage } = usePageContext();
  // what the customer pays by card: the base amount plus any fee quoted for the selected provider
  const { totalAmount: amount } = usePayableAmount('CARD');
  const { changeCardDetails, cardNumber, cvv, expirationDate, cardBrand } =
    useCardContext();

  const cardHandler = React.useMemo(
    () => PaymentHandlerFactory.card(provider as PAYMENT_PROVIDER),
    [provider],
  );

  const formik = useFormik({
    initialValues: { pin: '' },
    validate: (values) => {
      const result = validatePin(values);
      return result.isValid ? {} : result.errors;
    },
    onSubmit: async (values, { setSubmitting }) => {
      changeCardDetails({ pin: values.pin });

      try {
        const cardData: CardData = {
          cardNumber,
          expirationDate,
          cvv,
          pin: values.pin,
        };

        const response = await cardHandler.processPin({
          pin: values.pin,
          reference: ceviantTransactionId,
          ceviantTransactionId,
          provider,
          transactionRef: ceviantTransactionId,
          email: customerEmail,
          amount,
          cardData,
          cardType: cardBrand,
          publicKey: merchantCollectionPublicKey,
          vendorId: publicKey,
          mobile: customerPhone,
        });

        if (isErrorBody(response)) {
          changeSub(PAGE_SUB.ERROR, getResponseMessage(response));
          return;
        }

        // FirstCheckout answers PENDING and sends the cardholder an OTP; its
        // own reference, not the Ceviant id, completes the payment.
        if (provider === PAYMENT_PROVIDER.FIRSTCHEKOUT) {
          changeTransactionDetails({
            providerTransactionRef: response.transactionReference,
          });
          changeSub(PAGE_SUB.OTP_AUTH);
          return;
        }

        if (response) {
          changeCardDetails({
            otpPrompt: response.metadata?.displayText ?? '',
          });

          const redirectUrl =
            response.redirectUrl || response.data?.redirectHtml || response.url;

          if (redirectUrl) {
            setUrl(redirectUrl);

            if (provider === PAYMENT_PROVIDER.PAYLOFT) {
              changeSub(PAGE_SUB.BANK_AUTH);
              return;
            }
          }

          checkResponse(
            response.data?.code || response.response,
            changeSub,
            changePage,
            getResponseMessage(response),
          );
        }
      } catch (error) {
        // Not every handler unwraps axios errors, so read the API's own
        // message (e.g. "Incorrect PIN") rather than axios' status text.
        changeSub(PAGE_SUB.ERROR, getErrorMessage(error));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = e.target.value.replace(/\D/g, '').slice(0, 4);
    formik.setFieldValue('pin', sanitized);
  };

  const isButtonDisabled =
    formik.isSubmitting || !formik.isValid || !formik.dirty;

  return (
    <div className="space-y-6 text-center py-14">
      <img
        src="/icons/authenticate.svg"
        alt=""
        className="m-auto w-11"
      />
      <h3 className="text-[#535353] text-lg font-semibold">
        Please input your pin to continue your payment
      </h3>
      <form
        className="flex flex-col gap-5 px-4"
        onSubmit={formik.handleSubmit}
      >
        <FloatingLabelInput
          label="PIN"
          type="text"
          inputMode="numeric"
          value={formik.values.pin}
          onChange={handlePinChange}
          onBlur={formik.handleBlur}
          name="pin"
        />
        {formik.touched.pin && formik.errors.pin && (
          <p className="text-red-500 text-sm mt-1">{formik.errors.pin}</p>
        )}
        <div className="flex gap-3 w-full m-auto">
          <Button
            type="submit"
            variant="primary"
            disabled={isButtonDisabled}
          >
            {formik.isSubmitting ? (
              <Icon
                svg={Spinner}
                size={28}
                className="mx-auto text-white"
              />
            ) : (
              'Authenticate'
            )}
          </Button>
          <Button
            variant="outline"
            type="button"
            onClick={() =>
              window.parent.postMessage({ message: 'closeCeviantSDK' }, '*')
            }
            disabled={formik.isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PinAuth;
